import type { Widget, WidgetOutput } from "../types/widget";
import {
  buildWidgetChartMapping,
  mappingToJson,
  parseWidgetChartMapping,
} from "./chartMappingBuilder";
import {
  DEFAULT_ECHARTS_MAPPING_JSON,
  DEFAULT_TABLE_MAPPING_JSON,
  DEFAULT_TEXT_TEMPLATE,
} from "./widgetDraftDefaults";
import { validateWidgetDraftForSave } from "./widgetDraftValidation";

export type DraftOutputFormat = NonNullable<WidgetOutput["format"]>;

export function draftNeedsOutputApproval(widget: Widget): boolean {
  const format = widget.output?.format?.trim();
  if (!format) return true;
  return validateWidgetDraftForSave(widget).length > 0;
}

/** True when chat should auto-open the full editor (missing DSL, indices, or output). */
export function draftNeedsApproval(widget: Widget): boolean {
  const indices = widget.dataSource?.indices?.filter((i) => i?.trim()) ?? [];
  if (indices.length === 0) return true;
  const dsl = widget.dataSource?.dslTemplate;
  if (!dsl || typeof dsl !== "object" || Object.keys(dsl).length === 0) return true;
  return draftNeedsOutputApproval(widget);
}

export function inferDraftOutputFormat(widget: Widget): DraftOutputFormat {
  const explicit = widget.output?.format?.trim().toLowerCase();
  if (explicit === "table" || explicit === "echarts" || explicit === "text" || explicit === "html") {
    return explicit;
  }

  const mapping = widget.output?.mapping;
  if (mapping && typeof mapping === "object") {
    if (Array.isArray(mapping.columns)) return "table";
    const chartType =
      mapping.chartType != null ? String(mapping.chartType).trim().toLowerCase() : "";
    if (chartType === "table") return "table";
    if (mapping.optionTemplate != null) return "echarts";
    if (mapping.template != null) return "text";
  }

  return "echarts";
}

function parseJsonObject(raw: string): Record<string, unknown> {
  const parsed = JSON.parse(raw) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("mapping must be a JSON object");
  }
  return parsed as Record<string, unknown>;
}

export function defaultMappingForFormat(
  format: DraftOutputFormat,
  widgetName?: string
): Record<string, unknown> {
  switch (format) {
    case "table":
      return parseJsonObject(DEFAULT_TABLE_MAPPING_JSON);
    case "text":
    case "html":
      return {
        template: DEFAULT_TEXT_TEMPLATE.replace("{{widget.name}}", widgetName || "Widget"),
      };
    case "echarts":
    default: {
      const chart = buildWidgetChartMapping("bar", "vertical", widgetName || "");
      return JSON.parse(mappingToJson(chart)) as Record<string, unknown>;
    }
  }
}

function sanitizeMappingForFormat(
  format: DraftOutputFormat,
  mapping: Record<string, unknown>,
  widgetName?: string
): Record<string, unknown> {
  if (format === "table") {
    const columns = mapping.columns;
    const next: Record<string, unknown> = {};
    if (Array.isArray(columns) && columns.length > 0) {
      next.columns = columns;
    } else {
      Object.assign(next, defaultMappingForFormat("table", widgetName));
    }
    if (mapping.rowSource != null) next.rowSource = mapping.rowSource;
    return next;
  }

  if (format === "text" || format === "html") {
    const template = mapping.template;
    if (template != null && String(template).trim() !== "") {
      return { template: String(template) };
    }
    return defaultMappingForFormat(format, widgetName);
  }

  const chartType =
    mapping.chartType != null ? String(mapping.chartType).trim().toLowerCase() : "";
  if (chartType === "table" || mapping.optionTemplate == null) {
    return defaultMappingForFormat("echarts", widgetName);
  }
  return JSON.parse(mappingToJson(parseWidgetChartMapping(mapping))) as Record<string, unknown>;
}

export function resolveEditableDraftOutput(widget: Widget): {
  format: DraftOutputFormat;
  mapping: Record<string, unknown>;
} {
  const format = inferDraftOutputFormat(widget);
  const raw = widget.output?.mapping;
  const base =
    raw && typeof raw === "object" && !Array.isArray(raw)
      ? ({ ...raw } as Record<string, unknown>)
      : {};
  return {
    format,
    mapping: sanitizeMappingForFormat(format, base, widget.name),
  };
}

export function applyDraftOutputEdits(
  widget: Widget,
  format: DraftOutputFormat,
  mapping: Record<string, unknown>
): Widget {
  return {
    ...widget,
    output: {
      format,
      mapping: sanitizeMappingForFormat(format, mapping, widget.name),
    },
  };
}

export function mappingToEditorText(mapping: Record<string, unknown>): string {
  return JSON.stringify(mapping, null, 2);
}

export function parseMappingEditorText(text: string): Record<string, unknown> {
  return parseJsonObject(text);
}
